import {
  Badge,
  Box,
  Button,
  ButtonGroup,
  chakra,
  Flex,
  FormLabel,
  HStack,
  Icon,
  IconButton,
  Link,
  LinkBox,
  LinkOverlay,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { parseAsInteger, useQueryState } from "nuqs";
import { useCallback, useState } from "react";
import { RiExpandLeftRightLine, RiInformationLine } from "react-icons/ri";
import { Link as RouterLink } from "react-router-dom";
import Spinner from "../../components/chakra/common/Spinner";
import DebouncedInput from "../../components/DebouncedInput";
import { RefreshIcon } from "../../constants/commonIcons";
import { UserServicePermissions } from "../../shared/v2/constants/ServicePermissions";
import { api } from "../../utilities/apiRequest";
import { formatDateSinceNow } from "../../utilities/dateUtils";
import NotFoundScreen from "../NotFound";
import ServiceScreenContainer from "./ServiceScreenContainer";
import { useScreenExpansion } from "./useScreenExpansion";

type Props = {
  servicePermissions: UserServicePermissions;
};

export default function ServiceSignalsScreen({
  servicePermissions,
}: Props) {
  // Query params
  const apiUtils = api.useUtils();
  const [page, setPage] = useQueryState("page", parseAsInteger.withDefault(0));

  const [isScreenExpanded, setIsScreenExpanded] = useScreenExpansion();

  const onClickExpandTable = () => {
    setIsScreenExpanded(!isScreenExpanded);
  };

  const handleClickRefresh = () => {
    apiUtils.admin.signal.invalidate();
  };

  const signalsQuery = api.admin.signal.list.useQuery({
    pagination: {
      page,
    },
  });

  if (!servicePermissions["access"]) {
    return <NotFoundScreen />;
  }


  const renderSearch = () => {
    return (
      <HStack w="100%" justify="space-between" align="end">
        <Flex gap={4} />
        <Box>
          <Button
            isLoading={signalsQuery.isRefetching}
            disabled={signalsQuery.isFetching}
            onClick={handleClickRefresh}
            leftIcon={<Icon as={RefreshIcon} />}
            variant="ghost"
            loadingText="Refreshing"
            size="sm">
            Refresh
          </Button>
          <IconButton
            onClick={onClickExpandTable}
            size="md"
            variant="ghost"
            aria-label="Expand/Collapse table"
            icon={<RiExpandLeftRightLine />}
          />
        </Box>
      </HStack>
    );
  };

  const renderPaginator = () => {
    const hasNext =
      !signalsQuery.isFetching && signalsQuery.data?.pagination.hasNext;
    const hasPrev = !signalsQuery.isFetching && page > 0;
    const pageSize = signalsQuery.data?.pagination.size || 0;
    const numResults = signalsQuery.data?.pagination.count || 0;
    const resultFrom = page * pageSize + 1;
    const resultTo = resultFrom + numResults - 1;
    const resultText = signalsQuery.isSuccess
      ? `${resultFrom} - ${resultTo}`
      : " - ";
    return (
      <HStack align="center">
        <Text>Results {resultText}</Text>
        <ButtonGroup size="sm" isAttached variant="outline">
          <Button
            onClick={() => {
              if (!signalsQuery.data) return;
              const toPage = signalsQuery.data.pagination.page - 1;
              setPage(toPage);
            }}
            isDisabled={!hasPrev}>
            Prev
          </Button>
          <Button
            onClick={() => {
              if (!signalsQuery.data) return;
              const toPage = signalsQuery.data.pagination.page + 1;
              setPage(toPage);
            }}
            isDisabled={!hasNext}>
            Next
          </Button>
        </ButtonGroup>
      </HStack>
    );
  };

  return (
    <ServiceScreenContainer
      isStatic
      pageTitle="Account Upgrades"
      size={isScreenExpanded ? "full" : "lg"}
      breadcrumbs={[{ label: "Service Portal", link: "/_ccserviceportal" }]}>
      <VStack align="start" w="100%" h="100%" py="2">
        {renderSearch()}
        <TableContainer
          flexGrow="1"
          h="100%"
          w="full"
          overflowY="auto"
          borderRadius="lg"
          borderStyle="solid"
          borderWidth="1px">
          <Table
            isFetching={signalsQuery.isFetching}
            variant="data-table"
            size="sm"
            position="relative"
            sx={{
              "& thead tr th": {
                paddingTop: 2,
                paddingBottom: 2,
                position: "sticky",
                top: 0,
                backgroundColor: "white",
              },
            }}>
            <Thead zIndex="4" position="relative" maxH="fit-content">
              <Tr>
                <Th />
                <Th>Signal ID</Th>
                <Th>Account</Th>
                <Th>Event</Th>
                <Th>When</Th>
              </Tr>
            </Thead>
            {signalsQuery.isLoading && (
              <Tbody h="100%">
                <Tr h="100%">
                  <Td colSpan={5}>
                    <Flex
                      justifyContent="center"
                      alignItems="start"
                      justify="center"
                      h="100%">
                      <Flex h="70%" alignItems="center" justify="center">
                        <Spinner size="xl" />
                      </Flex>
                    </Flex>
                  </Td>
                </Tr>
              </Tbody>
            )}

            {signalsQuery.isSuccess && (
              <>
                <Tbody>
                  {signalsQuery.data?.signals.map((signal) => (
                    <Tr key={signal.signalID} maxH="fit-content">
                      <Td></Td>
                      <Td>
                        <Text
                          color="gray.600"
                          fontWeight="300"
                          fontStyle="italic">
                          {signal.signalID}
                        </Text>
                      </Td>
                      <Td>
                        <Link
                          as={RouterLink}
                          to={`/_ccserviceportal/accounts/${signal.account.accountID}`}>
                          {signal.account.name}
                        </Link>
                      </Td>
                      <Td>
                        <Badge>{signal.code}</Badge>
                        <Text fontSize="sm">{signal.info}</Text>
                      </Td>
                      <Td>
                        <Tooltip
                          label={new Date(
                            signal.whenTs,
                          ).toLocaleString()}>
                          <HStack as="span" align="center">
                            <Box as="span" textTransform="capitalize">
                              {formatDateSinceNow(
                                new Date(signal.whenTs),
                              )}
                            </Box>
                            <Icon
                              boxSize={4}
                              color="blue.300"
                              as={RiInformationLine}
                            />
                          </HStack>
                        </Tooltip>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>

                {/* <Tfoot>
                  <Tr>
                    <Th>User</Th>
                    <Th>Primary Email</Th>
                    <Th>Tags</Th>
                    <Th>Last Active</Th>
                    <Th>Last Sign-in</Th>
                    <Th>Accounts</Th>
                  </Tr>
                </Tfoot> */}
              </>
            )}
          </Table>
        </TableContainer>
        <HStack justify="end" w="100%">
          <ButtonGroup size="sm" variant="outline" isAttached>
            {renderPaginator()}
          </ButtonGroup>
        </HStack>
      </VStack>
    </ServiceScreenContainer>
  );
}
